import { useEffect, useMemo, useState } from "react";
import {
  TrendingUp,
  Users,
  IndianRupee,
  Timer,
  Activity,
  Layers,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { listTokenHistory, listServices } from "../lib/api";

const GOLD = "#E6C662";
const GOLD_DIM = "#9a7f33";
const EMERALD = "#34d399";

export default function AnalyticsPanel({ shopId }) {
  const [tokens, setTokens] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!shopId) return;
    setLoading(true);
    Promise.all([
      listTokenHistory(shopId, { days: 7 }),
      listServices(shopId),
    ])
      .then(([t, s]) => {
        setTokens(t);
        setServices(s);
      })
      .finally(() => setLoading(false));
  }, [shopId]);

  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTokens = tokens.filter((t) => new Date(t.created_at) >= today);
    const completedToday = todayTokens.filter((t) => t.status === "completed");
    const priceMap = Object.fromEntries(services.map((s) => [s.id, Number(s.price) || 0]));
    const revenueToday = completedToday.reduce(
      (sum, t) => sum + (priceMap[t.service_id] || 0),
      0
    );
    const durations = completedToday
      .filter((t) => t.serving_started_at && t.completed_at)
      .map((t) => (new Date(t.completed_at) - new Date(t.serving_started_at)) / 60000);
    const avgDuration = durations.length
      ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length)
      : 0;

    // top services last 7 days
    const svcCount = {};
    for (const t of tokens) {
      if (t.status !== "completed") continue;
      const key = t.service_name || "Other";
      svcCount[key] = (svcCount[key] || 0) + 1;
    }
    const topServices = Object.entries(svcCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, count]) => ({ name, count }));

    // hourly distribution today (24 buckets)
    const hourly = Array.from({ length: 24 }, (_, h) => ({
      h,
      label: `${String(h).padStart(2, "0")}h`,
      count: 0,
    }));
    for (const t of todayTokens) {
      hourly[new Date(t.created_at).getHours()].count += 1;
    }
    const peakHour = hourly.reduce(
      (best, cur) => (cur.count > best.count ? cur : best),
      { h: 0, count: 0, label: "—" }
    );

    // 7-day trend (today inclusive)
    const dayMs = 86400_000;
    const trend = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(today.getTime() - (6 - i) * dayMs);
      const next = new Date(d.getTime() + dayMs);
      const inDay = tokens.filter((t) => {
        const ts = new Date(t.created_at);
        return ts >= d && ts < next;
      });
      return {
        label: d.toLocaleDateString("en-IN", { weekday: "short" }),
        date: d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
        total: inDay.length,
        completed: inDay.filter((t) => t.status === "completed").length,
      };
    });

    return {
      todayCount: todayTokens.length,
      completedTodayCount: completedToday.length,
      revenueToday,
      avgDuration,
      topServices,
      hourly,
      peakHour,
      trend,
    };
  }, [tokens, services]);

  if (loading) {
    return (
      <div className="rounded-2xl bg-charcoal border border-white/10 p-6 text-white/40 text-sm">
        Loading analytics…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard icon={Users} label="Tokens Today" value={stats.todayCount} />
        <KpiCard icon={TrendingUp} label="Completed" value={stats.completedTodayCount} accent />
        <KpiCard
          icon={IndianRupee}
          label="Revenue Today"
          value={`₹${stats.revenueToday.toLocaleString()}`}
          accent
        />
        <KpiCard
          icon={Timer}
          label="Avg Service"
          value={stats.avgDuration ? `${stats.avgDuration}m` : "—"}
        />
        <KpiCard
          icon={Activity}
          label="Peak Hour"
          value={stats.peakHour.count > 0 ? stats.peakHour.label : "—"}
        />
      </div>

      {/* Charts row 1: 7-day trend area chart */}
      <PremiumCard
        title="7-Day Token Trend"
        subtitle="Total vs Completed"
        icon={Layers}
      >
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={stats.trend}
              margin={{ top: 10, right: 16, left: -8, bottom: 0 }}
            >
              <defs>
                <linearGradient id="gradGold" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={GOLD} stopOpacity={0.55} />
                  <stop offset="100%" stopColor={GOLD} stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="gradEmerald" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={EMERALD} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={EMERALD} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis
                dataKey="label"
                stroke="rgba(255,255,255,0.45)"
                tick={{ fontSize: 11, fontFamily: "IBM Plex Mono, monospace" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                stroke="rgba(255,255,255,0.35)"
                tick={{ fontSize: 11, fontFamily: "IBM Plex Mono, monospace" }}
                axisLine={false}
                tickLine={false}
                width={32}
                allowDecimals={false}
              />
              <Tooltip
                cursor={{ fill: "rgba(230,198,98,0.06)" }}
                contentStyle={{
                  background: "rgba(8,8,8,0.95)",
                  border: "1px solid rgba(230,198,98,0.35)",
                  borderRadius: 12,
                  fontFamily: "IBM Plex Mono, monospace",
                  fontSize: 12,
                  letterSpacing: "0.06em",
                }}
                labelStyle={{ color: GOLD, fontWeight: 600 }}
                itemStyle={{ color: "#fff" }}
              />
              <Area
                type="monotone"
                dataKey="total"
                stroke={GOLD}
                strokeWidth={2}
                fill="url(#gradGold)"
                name="Total"
              />
              <Area
                type="monotone"
                dataKey="completed"
                stroke={EMERALD}
                strokeWidth={2}
                fill="url(#gradEmerald)"
                name="Completed"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 flex items-center gap-5 text-[10px] font-numbers tracking-[0.25em] uppercase text-white/55">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: GOLD }} /> Total
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: EMERALD }} /> Completed
          </span>
        </div>
      </PremiumCard>

      {/* Charts row 2: Hourly bar + Top services */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PremiumCard
          title="Hourly Flow · Today"
          subtitle={
            stats.peakHour.count > 0
              ? `Peak ${stats.peakHour.label} · ${stats.peakHour.count} tokens`
              : "No data yet"
          }
          icon={Activity}
        >
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={stats.hourly}
                margin={{ top: 10, right: 6, left: -10, bottom: 0 }}
                barCategoryGap="18%"
              >
                <defs>
                  <linearGradient id="barGold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={GOLD} stopOpacity={1} />
                    <stop offset="100%" stopColor={GOLD_DIM} stopOpacity={0.85} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis
                  dataKey="h"
                  stroke="rgba(255,255,255,0.4)"
                  tick={{ fontSize: 10, fontFamily: "IBM Plex Mono, monospace" }}
                  interval={2}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${v}h`}
                />
                <YAxis
                  stroke="rgba(255,255,255,0.35)"
                  tick={{ fontSize: 10, fontFamily: "IBM Plex Mono, monospace" }}
                  axisLine={false}
                  tickLine={false}
                  width={28}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ fill: "rgba(230,198,98,0.06)" }}
                  contentStyle={{
                    background: "rgba(8,8,8,0.95)",
                    border: "1px solid rgba(230,198,98,0.35)",
                    borderRadius: 10,
                    fontFamily: "IBM Plex Mono, monospace",
                    fontSize: 12,
                  }}
                  labelFormatter={(v) => `${String(v).padStart(2, "0")}:00`}
                  labelStyle={{ color: GOLD, fontWeight: 600 }}
                  itemStyle={{ color: "#fff" }}
                />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="url(#barGold)">
                  {stats.hourly.map((entry) => (
                    <Cell
                      key={`c-${entry.h}`}
                      fill={
                        entry.h === stats.peakHour.h && entry.count > 0
                          ? GOLD
                          : "url(#barGold)"
                      }
                      opacity={entry.h === stats.peakHour.h ? 1 : 0.9}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </PremiumCard>

        <PremiumCard
          title="Top Services · 7 Days"
          subtitle={`${stats.topServices.length} categories`}
          icon={Layers}
        >
          <div className="mt-1 space-y-3">
            {stats.topServices.length === 0 && (
              <div className="text-white/35 text-sm py-6">No completed services yet.</div>
            )}
            {stats.topServices.map(({ name, count }, idx) => {
              const max = stats.topServices[0]?.count || 1;
              const pct = (count / max) * 100;
              return (
                <div key={name}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/85 truncate pr-3">
                      <span className="text-gold/70 font-numbers mr-2">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      {name}
                    </span>
                    <span className="font-numbers text-gold tabular-nums">{count}</span>
                  </div>
                  <div className="mt-1.5 h-2 rounded-full bg-white/[0.06] overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${pct}%`,
                        background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_DIM} 100%)`,
                        boxShadow: `0 0 18px ${GOLD}55`,
                        transition: "width 600ms cubic-bezier(0.22,1,0.36,1)",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </PremiumCard>
      </div>
    </div>
  );
}

function PremiumCard({ title, subtitle, icon: Icon, children }) {
  return (
    <div
      className="rounded-2xl border p-6 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, rgba(20,20,20,0.85) 0%, rgba(10,10,10,0.85) 100%)",
        borderColor: "rgba(230,198,98,0.18)",
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,0.05), 0 8px 32px -12px rgba(0,0,0,0.6)",
      }}
    >
      <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-gold/[0.06] blur-3xl pointer-events-none" />
      <div className="flex items-center justify-between gap-3 relative">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-3.5 h-3.5 text-gold" strokeWidth={1.5} />}
          <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-gold">
            {title}
          </div>
        </div>
        {subtitle && (
          <div className="font-numbers text-[10px] tracking-[0.25em] uppercase text-white/45">
            {subtitle}
          </div>
        )}
      </div>
      <div className="mt-5 relative">{children}</div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent }) {
  return (
    <div
      className="rounded-2xl border p-5 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, rgba(22,22,22,0.85) 0%, rgba(12,12,12,0.85) 100%)",
        borderColor: accent ? "rgba(230,198,98,0.28)" : "rgba(255,255,255,0.08)",
        boxShadow: accent
          ? "inset 0 1px 0 rgba(255,255,255,0.05), 0 6px 24px -10px rgba(230,198,98,0.3)"
          : "inset 0 1px 0 rgba(255,255,255,0.04)",
      }}
    >
      {accent && (
        <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
      )}
      <div className="relative flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-white/55 font-numbers">
        <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {label}
      </div>
      <div
        className={`relative mt-3 font-numbers text-2xl md:text-3xl tabular-nums ${
          accent ? "text-gold gold-text-glow" : "text-white"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
